import React, { useState, useCallback } from "react"
import { Row, Col } from '@qc/particle/lib/components/grid';
import Typography from '@qc/particle/lib/components/typography';
import Alert from '@qc/particle/lib/components/alert'
import Button from '@qc/particle/lib/components/button'
import Form from '@qc/particle/lib/components/form'
import Input from '@qc/particle/lib/components/input'
import Result from '@qc/particle/lib/components/result'
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk'

import { connect } from "react-redux";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope'

import { getForwardUrl } from "../oktaConfigUtil"
import { getValidRedirect } from '../utils/whiteListRedirect'
import * as Stld from "./SignUpView.styles";
import { registerUser } from "../store/reducers/user_registration/actions"
import CaptchaProtectedComponent from "../components/CaptchaProtectedComponent"
import QCPanelContainer from "../components/QCPanelContainer";

const { Paragraph } = Typography;

const successMessageView = (success, idpInitiated) => {
  if(!success) {
    return null;
  }
  if (idpInitiated) {
    return (
      <>
      <Result
        status="success"
        title="Ready to go!"
        subTitle="Your email is configured to an external identity provider. You can directly login with your email."
        extra={[<Button type="primary" href="/user/login">Go to Login</Button>]} />
      </>
    )
  }

  return (
    <Result
      title="Check your inbox"
      icon={<FontAwesomeIcon icon={faEnvelope}/>}
      subTitle="We have sent you an email to activate this account. Please check your inbox for the invitation."
      extra={[<Button type="primary" href="/user/login">Back to Login</Button>]}
    />
  )
}

export const SignUpViewBase = ({
  saving,
  error,
  success,
  idpInitiated,
  registerUser,
  testMode
}) => {
  const urlParams = new URLSearchParams(location.search);
  const loginHint = urlParams.get('login_hint');
  const [form] = Form.useForm();
  const forwardUrl = getForwardUrl();
  const redirectUrl = getValidRedirect(forwardUrl);
  const [recaptchaToken, setRecatpchaToken] = useState(null)
  const [recaptchaVersion, setRecatpchaVersion] = useState(null)
  const [signed, setSigned] = useState(testMode ? true : false)

  const _registerUser = (formData) => {
    registerUser(
      formData.email,
      formData.firstName,
      formData.lastName,
      redirectUrl,
      recaptchaVersion,
      recaptchaToken
    );
  };

  const onPass = useCallback((version, token) => {
    setRecatpchaVersion(version)
    setRecatpchaToken(token)
  }, [setRecatpchaToken, setRecatpchaVersion])

  let errorMessage = null
  if (error === "403") {
    errorMessage = <Alert message="Sorry, there was a problem with recatcpha. Please reload and try again" type="error" showIcon data-cy="errorMessageTokenExpired" />
  } else if (error === "409") {
    errorMessage = <Alert message="Sorry, this email is already taken. Please try another one." type="warning" showIcon data-cy="errorMessageTokenExpired" />
  } else if (error) {
    errorMessage = <Alert message="An error occurred when trying to activate your account. Please try again later." type="error" showIcon />
  }

  const successMessage = successMessageView(success, idpInitiated)

  return (
    <QCPanelContainer showMarketingMessage>
      <CaptchaProtectedComponent action="register" onPass={onPass} onFail={() => console.log("Captcha Failed")}>
        <Stld.Content>
          <Stld.Card>
            <div>
              {!successMessage ? <div data-cy="header">
                <Stld.H3 data-cy="qText">Create your Account</Stld.H3>
              </div>: null}
              {errorMessage}
              {successMessage ? successMessage : 
                <Form
                  requiredMark={false}
                  form={form}
                  layout="vertical"
                  onFinish={_registerUser}
                >
                  <Stld.FormControl
                    data-cy="emailAddressControl"
                    label="Email"
                    value={loginHint}
                    name="email"
                    validateTrigger="onBlur"
                    rules={[
                      {required: true, message: 'Email address is not valid' },
                      {type: "email", message: 'Email address is not valid'}
                    ]}
                    style={{height: "91px", marginBottom: "0px"}} // Removes a weird 'bounce' effect when addressing a problem with the user email
                  >
                    <Input data-testid="emailInput" id="emailInput" placeholder="Type in your email address" size="large" />
                  </Stld.FormControl>

                  <Stld.FormControl
                    data-cy="firstNameControl"
                    label="First Name"
                    name="firstName"
                    rules={[
                      {required: true, message: 'Please input your first name' },
                      {whitespace: true, message: 'First name cannot be a whitespace'}
                    ]}
                  >
                    <Input data-testid="firstNameInput" placeholder="Type in your name" size="large" />
                  </Stld.FormControl>
                  <Stld.FormControl
                    data-cy="lastNameControl"
                    label="Last Name"
                    name="lastName"
                    rules={[
                      {required: true, message: 'Please input your last name' },
                      {whitespace: true, message: 'First name cannot be a whitespace'}
                    ]}
                  >
                    <Input data-testid="lastNameInput" placeholder="Type in your last name" size="large" />
                  </Stld.FormControl>

                  <PSClickWrap
                    clickWrapStyle="combined"
                    accessId={process.env.REACT_APP_PACT_SAFE_ACCESS_ID || ''}
                    groupKey={process.env.REACT_APP_PACT_SAFE_GROUP_KEY || ''}
                    signerIdSelector={"emailInput"}
                    onValid={() => setSigned(true)}
                    onInvalid={() => setSigned(false)}
                  />
                  <Stld.FormControl>
                      <Button data-testid="submit" type="primary" htmlType="submit" loading={saving} disabled={saving || !signed} size="large" >
                          Save
                      </Button>
                  </Stld.FormControl>
                </Form>
              }
            </div>
          </Stld.Card>
        </Stld.Content>
      </CaptchaProtectedComponent>
    </QCPanelContainer>
  );
};

const mapStateToProps = state => ({
  saving: state.registration.loading,
  registration: state.registration,
  error: state.registration.error,
  success: state.registration.success,
  idpInitiated: state.registration.idpInitiated
})

const mapDispatchToProps = dispatch => ({
  registerUser: (email, firstName, lastName, redirectUrl, recaptchaVersion, recaptchaToken) =>
    dispatch(registerUser(email, firstName, lastName, redirectUrl, recaptchaVersion, recaptchaToken))
})

const SignupView = connect(mapStateToProps, mapDispatchToProps)(SignUpViewBase)

export default SignupView
